import { createSlice } from '@reduxjs/toolkit';
import { RidesItemType } from '@component/Ride/types';
import { ridesApi } from '@store/rtk/rides';
import { removeSeconds } from '@component/RidesPage/RidesTable/constants';
import { daysObject, extraPassengersObject } from '@component/Ride/constants';
import { formatUTCToLocal, setStatusValueFromLabel } from '@src/utils';
import { isValidJson } from '@utils/helpers';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

const getSegmentDisplayOrder = (segment: any, fallbackIndex: number) => {
  const orderingValue = Number(segment?.orderingValue);

  return Number.isFinite(orderingValue) && orderingValue > 0
    ? orderingValue
    : fallbackIndex + 1;
};

const sortSegmentsForDisplay = (segments: any[] = []) => {
  const sortableSegments = new Array(segments.length);

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const numericId = Number(segment?.id);

    sortableSegments[index] = {
      segment,
      index,
      displayOrder: getSegmentDisplayOrder(segment, index),
      numericId: Number.isFinite(numericId) ? numericId : null,
    };
  }

  sortableSegments.sort((a, b) => {
    const orderingDiff = a.displayOrder - b.displayOrder;

    if (orderingDiff !== 0) {
      return orderingDiff;
    }

    if (
      a.numericId !== null &&
      b.numericId !== null &&
      a.numericId !== b.numericId
    ) {
      return a.numericId - b.numericId;
    }

    return a.index - b.index;
  });

  for (let index = 0; index < sortableSegments.length; index += 1) {
    sortableSegments[index] = sortableSegments[index].segment;
  }

  return sortableSegments;
};
const { segmentNotesColumns } = TABLE_COLUMNS;

const initialState: RidesItemType = {
  ridesData: [
    {
      rides: null,
      id: 68,
      modeOfTransport: 1,
      memberId: 3,
      payorId: 3,
      standingOrder: null,
      schedulerAuthorizedContact: 10,
      rideOrderConfirmationCode: '123123',
      rideRequestDate: '12/12/2024',
      ridesSegments: null,
      equipmentAddons: null,
      ridesTags: null,
    },
  ],
  segmentNotes: {
    columns: segmentNotesColumns,
    rows: [],
  },
  rideByIdData: null,
  standingOrderByIdData: null,
  generatedRides: [],
  isRideSaved: false,
  savedRideId: null,
  isSOSaved: false,
  savedSOId: null,
  isRideCopy: false,
  createSOByRide: false,
  isShowSO: false,
  isShowRide: false,
  willCallActivationTime: null,
  wcTime: '',
  draftRideFormValues: null,
  draftRideId: null,
  isRideDraftEditing: false,
  draftStandingOrderFormValues: null,
  draftStandingOrderId: null,
  lockedByInformation: null,
  isStandingOrderDraftEditing: false,
};

export const ridesSlice = createSlice({
  name: 'rides',
  initialState,
  reducers: {
    setIsRideSaved: (state, { payload }) => {
      state.isRideSaved = payload;
    },
    setSavedRideId: (state, { payload }) => {
      state.savedRideId = payload;
    },
    setIsSOSaved: (state, { payload }) => {
      state.isSOSaved = payload;
    },
    setSavedSOId: (state, { payload }) => {
      state.savedSOId = payload;
    },
    setIsRideCopy: (state, { payload }) => {
      state.isRideCopy = payload;
    },
    setCreateSOByRide: (state, { payload }) => {
      state.createSOByRide = payload;
    },
    setIsShowSO: (state, { payload }) => {
      state.isShowSO = payload;
    },
    setIsShowRide: (state, { payload }) => {
      state.isShowRide = payload;
    },
    setWillCallActivationTime: (state, { payload }) => {
      state.willCallActivationTime = payload;
    },
    setWcTime: (state, { payload }) => {
      state.wcTime = payload;
    },
    cleanRideByIdData: (state) => {
      state.rideByIdData = null;
    },
    setRideDraft: (state, { payload }) => {
      state.draftRideFormValues = payload.values;
      state.draftRideId = payload.rideId;
      state.isRideDraftEditing = payload.isEditing;
    },
    clearRideDraft: (state) => {
      state.draftRideFormValues = null;
      state.draftRideId = null;
      state.isRideDraftEditing = false;
    },
    setStandingOrderDraft: (state, { payload }) => {
      state.draftStandingOrderFormValues = payload.values;
      state.draftStandingOrderId = payload.standingOrderId;
      state.isStandingOrderDraftEditing = payload.isEditing;
    },
    clearStandingOrderDraft: (state) => {
      state.draftStandingOrderFormValues = null;
      state.draftStandingOrderId = null;
      state.isStandingOrderDraftEditing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        ridesApi.endpoints.getRides.matchFulfilled,
        (state, { payload }) => {
          state.ridesData = [...payload];
        }
      )
      .addMatcher(
        ridesApi.endpoints.getRideById.matchFulfilled,
        (state, { payload }) => {
          if (payload) {
            const { extraPassenger, rideRequestDate, ridesSegments } = payload;
            let currentDate = rideRequestDate;
            const sortedRideSegments = sortSegmentsForDisplay(
              ridesSegments || []
            );

            const segments = [
              ...sortedRideSegments?.map((segment, idx: number, arr) => {
                let nextDay = false;
                const {
                  scheduledAppointmentTime,
                  scheduledPickupTime,
                  driver,
                  vehicle,
                  estMiles,
                  estimatedTripDuration,
                  status,
                  rideDate,
                  ...rest
                } = segment;

                if (new Date(rideDate) > new Date(currentDate)) {
                  nextDay = true;
                  currentDate = rideDate;
                }

                return {
                  ...rest,
                  scheduledAppointmentTime: scheduledAppointmentTime
                    ? removeSeconds(scheduledAppointmentTime)
                    : null,
                  scheduledPickupTime: scheduledPickupTime
                    ? removeSeconds(scheduledPickupTime)
                    : null,
                  driver: isValidJson(driver) ? JSON.parse(driver) : {},
                  vehicle: isValidJson(vehicle) ? JSON.parse(vehicle) : {},
                  estimatedTripDuration: removeSeconds(estimatedTripDuration),
                  estMiles: `${estMiles} Mi`,
                  rideRemoteId: rest.id,
                  status: setStatusValueFromLabel(status),
                  nextDay,
                  rideDate,
                };
              }),
            ];

            state.rideByIdData = {
              ...payload,
              ridesSegments: segments,
              extraPassenger: extraPassenger
                ? {
                    id: extraPassenger,
                    label: extraPassenger,
                    value: extraPassenger,
                  }
                : extraPassengersObject[0],
            };
          }
        }
      )
      .addMatcher(
        ridesApi.endpoints.getStandingOrderById.matchFulfilled,
        (state, { payload }) => {
          const { extraPassenger, startDate, ridesSegments, scheduledDays } =
            payload;
          const newScheduledDays = scheduledDays?.map((el) => daysObject[el]);

          let currentDate = startDate;
          const sortedRideSegments = sortSegmentsForDisplay(
            ridesSegments || []
          );
          const segments = [
            ...sortedRideSegments?.map((segment, idx: number, arr) => {
              let nextDay = false;
              const {
                scheduledAppointmentTime,
                scheduledPickupTime,
                driver,
                vehicle,
                estMiles,
                estimatedTripDuration,
                status,
                rideDate,
                ...rest
              } = segment;
              if (new Date(segment.rideDate) > new Date(currentDate)) {
                nextDay = true;
                currentDate = segment.rideDate;
              }

              return {
                ...rest,
                scheduledAppointmentTime: scheduledAppointmentTime
                  ? removeSeconds(scheduledAppointmentTime)
                  : null,
                scheduledPickupTime: scheduledPickupTime
                  ? removeSeconds(scheduledPickupTime)
                  : null,
                estimatedTripDuration: removeSeconds(estimatedTripDuration),
                estMiles: `${estMiles} Mi`,
                rideRemoteId: rest.id,
                status: setStatusValueFromLabel(status),
                nextDay,
                rideDate: rideDate ?? currentDate,
              };
            }),
          ];

          state.standingOrderByIdData = {
            ...payload,
            ridesSegments: segments,
            extraPassenger: extraPassenger
              ? {
                  id: extraPassenger,
                  label: extraPassenger,
                  value: extraPassenger,
                }
              : extraPassengersObject[0],
            scheduledDays: newScheduledDays,
          };
        }
      )
      .addMatcher(
        ridesApi.endpoints.getRideSegmentNotes.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.segmentNotes = {
              ...state.segmentNotes,
              rows: payload.content.map((el) => ({
                ...el,
                actionTime: formatUTCToLocal(el.actionTime),
              })),
              ...payload,
              requestTablePageChange: ridesApi.endpoints.getRideSegmentNotes,
            };
          }
        }
      )
      .addMatcher(
        ridesApi.endpoints.getGeneratedRide.matchFulfilled,
        (state, { payload }) => {
          state.generatedRides = payload;
        }
      )
      .addMatcher(
        ridesApi.endpoints.checkLockSegmentStatus.matchFulfilled,
        (state, { payload }) => {
          state.lockedByInformation = payload.results[0];
        }
      );
  },
});

export const selectRides = (state) => state.rides;

export const {
  setIsRideSaved,
  setSavedRideId,
  setIsSOSaved,
  setSavedSOId,
  setIsRideCopy,
  setCreateSOByRide,
  setIsShowSO,
  setIsShowRide,
  setWillCallActivationTime,
  setWcTime,
  cleanRideByIdData,
  setRideDraft,
  clearRideDraft,
  setStandingOrderDraft,
  clearStandingOrderDraft,
} = ridesSlice.actions;
export const ridesReducer = ridesSlice.reducer;
